// src/pages/Wallet/modals/PortfolioChartModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BiInfoCircle } from 'react-icons/bi';
import { MdOutlineShowChart } from 'react-icons/md';
import './PortfolioChartModal.css';

const PortfolioChartModal = ({ isOpen, onClose, portfolioData, currentValue }) => {
  const { t } = useTranslation();
  const [selectedTimeframe, setSelectedTimeframe] = useState('24h');
  const [chartData, setChartData] = useState([]);
  const [priceChange, setPriceChange] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Process chart data based on selected timeframe
  const processChartData = useCallback((data, timeframe) => {
    if (!data || data.length === 0) return [];
    
    let processedData = [...data];
    
    // If we have current value but it's not already in the data, add it
    if (currentValue > 0 && processedData.length > 0) {
      const lastDataPoint = processedData[processedData.length - 1];
      const now = Date.now();
      
      // Only add if the last data point is older than 2 minutes
      if (now - lastDataPoint.timestamp > 2 * 60 * 1000) {
        processedData.push({
          timestamp: now,
          value: currentValue
        });
      }
    }
    
    return processedData;
  }, [currentValue]);
  
  // Calculate price change percentage
  const calculatePriceChange = useCallback((data) => {
    if (!data || data.length < 2) return null;
    
    const firstValue = data[0].value;
    const lastValue = data[data.length - 1].value;
    
    if (firstValue <= 0) return null;
    
    return ((lastValue - firstValue) / firstValue) * 100;
  }, []);
  
  // Update data when timeframe or portfolio data changes
  useEffect(() => {
    setIsLoading(true);
    
    if (!portfolioData || portfolioData.length === 0) {
      setChartData([]);
      setPriceChange(null);
      setIsLoading(false);
      return;
    }
    
    // Process data based on timeframe
    const processed = processChartData(portfolioData, selectedTimeframe);
    setChartData(processed);
    
    // Calculate price change
    const change = calculatePriceChange(processed);
    setPriceChange(change);
    
    setIsLoading(false);
  }, [portfolioData, selectedTimeframe, processChartData, calculatePriceChange]);
  
  // Get chart color based on price trend
  const getChartColor = () => {
    if (priceChange === null) return "var(--text-tertiary)";
    return priceChange >= 0 ? "var(--success)" : "var(--error)";
  };
  
  // Format X-axis ticks based on the selected timeframe
  const formatXAxis = (timestamp) => {
    const date = new Date(timestamp);
    
    switch (selectedTimeframe) {
      case '24h':
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      case '1w':
        return `${date.getMonth() + 1}/${date.getDate()}`;
      case '1m':
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      default:
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  };
  
  // Determine the most recent value or use the provided current value
  const mostRecentValue = () => {
    if (chartData.length > 0) {
      return chartData[chartData.length - 1].value;
    }
    return currentValue || 0;
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="portfolio-chart-modal-overlay">
      <div className="portfolio-chart-modal">
        <div className="portfolio-chart-header">
          <h2>{t('wallet.portfoliochart.title', 'Portfolio History')}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="portfolio-chart-content-balance">
          {/* Current Balance Display */}
          <div className="portfolio-chart-balance">
            <div className="portfolio-balance-value">${mostRecentValue().toFixed(2)}</div>
            {priceChange !== null && (
              <div className={`portfolio-change-balance ${priceChange >= 0 ? 'positive' : 'negative'}`}>
                {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
              </div>
            )}
          </div>
          
          {/* Timeframe Selector */}
          <div className="timeframe-tabs">
            <button 
              className={`timeframe-button ${selectedTimeframe === '24h' ? 'active' : ''}`} 
              onClick={() => setSelectedTimeframe('24h')}
            >
              24H
            </button>
            <button 
              className={`timeframe-button ${selectedTimeframe === '1w' ? 'active' : ''}`} 
              onClick={() => setSelectedTimeframe('1w')}
            >
              1W
            </button>
            <button 
              className={`timeframe-button ${selectedTimeframe === '1m' ? 'active' : ''}`} 
              onClick={() => setSelectedTimeframe('1m')}
            >
              1M
            </button>
          </div>
          
          {/* Chart Container */}
          <div className="portfolio-chart-container">
            {isLoading ? (
              <div className="chart-loading">
                <div className="loading-spinner"></div>
                <p>{t('wallet.portfoliochart.loading', 'Loading chart data...')}</p>
              </div>
            ) : chartData.length > 1 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis 
                    dataKey="timestamp" 
                    tickFormatter={formatXAxis}
                    stroke="var(--text-tertiary)"
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    domain={['auto', 'auto']} 
                    tickFormatter={(value) => `$${value.toFixed(2)}`}
                    width={80}
                    stroke="var(--text-tertiary)"
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip 
                    labelFormatter={(timestamp) => {
                      const date = new Date(timestamp);
                      return date.toLocaleString();
                    }}
                    formatter={(value) => [`$${parseFloat(value).toFixed(2)}`, 'Portfolio Value']}
                    contentStyle={{ 
                      backgroundColor: 'var(--surface-bg)',
                      borderColor: 'var(--border-color)',
                      borderRadius: 'var(--border-radius-sm)'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke={getChartColor()} 
                    dot={false} 
                    strokeWidth={2}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-chart-data">
                <MdOutlineShowChart size={40} color="var(--text-tertiary)" />
                <p>{t('wallet.portfoliochart.notEnoughData', 'Not enough historical data available.')}</p>
                {chartData.length === 0 && (
                  <p>{t('wallet.portfoliochart.dataExplanation', 'Portfolio data is collected while the app is open. Visit regularly for better tracking.')}</p>
                )}
              </div>
            )}
          </div>
          
          {/* Data Collection Info */}
          <div className="data-collection-info">
            <BiInfoCircle size={20} />
            <div>
              <p>
                {t('wallet.portfoliochart.dataCollectionInfo', 'Portfolio data is collected approximately every 5 minutes while the app is open. For more accurate tracking, open the app at least once daily.')}
              </p>
              <p className="data-collection-note">
                {t('wallet.portfoliochart.dataNote', 'Note: We wait 10 seconds after each portfolio update to ensure all token balances are properly loaded before caching.')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioChartModal;